import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { motion, AnimatePresence } from 'framer-motion';
import { initiateLinkedInLogin } from '../utils/linkedinAuth';
import { API_ENDPOINTS } from '../config/api';
import './SignUp.css';

const SignUp = ({ responseGoogle }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  const [referralValid, setReferralValid] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  
  const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID;

  const LINKEDIN_REDIRECT_URI = `${window.location.origin}/linkedin-callback`;
  const LINKEDIN_SCOPE = 'openid profile email';
  
  useEffect(() => {
    if (localStorage.getItem('isLoggedIn') === 'true') {
      navigate('/tool');
    }

    const refCode = searchParams.get('ref');
    if (refCode) {
      validateReferralCode(refCode);
      sessionStorage.setItem('pending_referral_code', refCode);
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state) {
      navigate(`/linkedin-callback?code=${code}&state=${state}`);
      return;
    }
    
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [navigate, searchParams]);

  const validateReferralCode = async (code) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.VALIDATE_REFERRAL_CODE}?referral_code=${encodeURIComponent(code)}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.is_valid) {
          setReferralCode(code);
          setReferralValid(true);
          setReferralInfo(data);
        }
      }
    } catch (error) {
      // Silent fail
    }
  };

  const handleLinkedInLogin = () => {
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    if (!redirectPath) {
      localStorage.setItem('redirectAfterLogin', '/tool');
    }
    initiateLinkedInLogin();
  };

  const particles = Array.from({ length: 15 }, (_, i) => i + 1);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <AnimatePresence>
      {loading ? (
        <motion.div 
          className="loading-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          key="loader"
        >
          <motion.div 
            className="loading-logo"
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 0, 360],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          >
            <img src="/logo.png" alt="Jobque Logo" />
          </motion.div>
        </motion.div>
      ) : (
        <motion.div 
          className="signup-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          key="content"
        >
          <div className="signup-left">
            <div className="illustration-container">
              <motion.img
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 1, 
                  delay: 0.3,
                  type: "spring",
                  stiffness: 100
                }}
                src="/Signup.png"
                alt="Job Search Illustration"
                className="signup-illustration"
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              />
            </div>
            
            <div className="particles">
              {particles.map((i) => (
                <motion.div 
                  key={i} 
                  className={`particle particle-${i}`}
                  initial={{ 
                    scale: Math.random() * 0.5 + 0.5,
                    x: Math.random() * 100 - 50,
                    y: Math.random() * 100 - 50,
                    opacity: Math.random() * 0.3 + 0.1
                  }}
                  animate={{ 
                    x: [0, Math.random() * 100 - 50, 0],
                    y: [0, Math.random() * 100 - 50, 0],
                    opacity: [0.1, 0.6, 0.1],
                    rotate: [0, 360]
                  }}
                  transition={{ 
                    duration: Math.random() * 10 + 15,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                ></motion.div>
              ))}
            </div>
          </div>
          
          <div className="signup-right">
            <motion.div
              className="signup-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.2,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                transition: { duration: 0.3 }
              }}
            >
              <motion.h1 
                className="signup-title"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <span className="text-gradient">Welcome to JobQue</span>
              </motion.h1>
              
              {referralValid && (
                <motion.div
                  className="referral-banner"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  🎉 <strong>Special Offer!</strong> You've been referred by a friend.
                  Get <strong>50% OFF</strong> your first month!
                </motion.div>
              )}
              
              <motion.p
                className="signup-subtitle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                Find your dream job faster with our AI-powered job search tool
              </motion.p>
              
              <motion.div
                className="features-list"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {[
                  "Automated job search across multiple platforms",
                  "Save hours of manual searching",
                  "Track your applications in one place"
                ].map((text, index) => (
                  <motion.div 
                    className="feature-item" 
                    key={index}
                    variants={itemVariants}
                    whileHover={{ x: 5, transition: { duration: 0.2 } }}
                  >
                    <motion.div 
                      className="feature-icon"
                      whileHover={{ 
                        scale: 1.2,
                        backgroundColor: "#07624c",
                        color: "#ffffff",
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      ✓
                    </motion.div>
                    <span>{text}</span>
                  </motion.div>
                ))}
              </motion.div>
              
              <motion.div
                className="signup-button-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1 }}
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="google-login-wrapper"
                >
                  <GoogleLogin
                    onSuccess={responseGoogle}
                    onFailure={(response) => console.error('Login Failed:', response)}
                    cookiePolicy={'single_host_origin'}
                    className="google-login-button"
                    scope="openid email profile"
                  />
                </motion.div>
                
                <motion.div
                  className="divider-container"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.1 }}
                >
                  <div className="divider-line"></div>
                  <span className="divider-text">or</span>
                  <div className="divider-line"></div>
                </motion.div>
                
                <button
                  className="linkedin-login-button"
                  onClick={handleLinkedInLogin}
                  type="button"
                >
                  <svg className="linkedin-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
                  </svg>
                  <span>Sign in with LinkedIn</span>
                </button>
                
                <motion.p 
                  className="terms-text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.2 }}
                >
                  By signing up, you agree to our{' '}
                  <motion.a 
                    href="/terms"
                    whileHover={{ 
                      color: "#07624c",
                      textDecoration: "underline" 
                    }}
                  >
                    Terms of Service
                  </motion.a>{' '}
                  and{' '}
                  <motion.a 
                    href="/privacy"
                    whileHover={{ 
                      color: "#07624c",
                      textDecoration: "underline" 
                    }}
                  >
                    Privacy Policy
                  </motion.a>
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SignUp;